import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Percent, Tablet, Users, type LucideIcon } from "lucide-react";
import { commissionPercentStringFromRate } from "@/lib/commissionRate";
import {
  getTelemetryStatus,
  telemetryStatusDotClass,
  type TelemetryStatus,
} from "@/lib/deviceStatus";
import { focusRingInsetClass } from "@/components/ui";
import { cn } from "@/lib/utils";
import { deviceKeys } from "@/services/deviceService";
import { houseService } from "@/services/houseService";
import type { House } from "@/types";

interface HouseOverviewStatsProps {
  house: House;
  houseId: string;
  /** Structure commission KPI — never shown to the host it pays. */
  showHostShare: boolean;
  /**
   * Fleet health (status dots + online count). Off for hosts: their
   * house-device projection carries no telemetry, so every tablet would read
   * as offline.
   */
  showTelemetry: boolean;
  /** Jump to the tab that explains a metric (devices / presences). */
  onOpenDevices: () => void;
  onOpenGuests: () => void;
}

const STATUS_ORDER: TelemetryStatus[] = ["green", "yellow", "red", "gray"];
const MAX_STATUS_DOTS = 16;

interface MetricProps {
  label: string;
  icon: LucideIcon;
  iconClassName: string;
  value: ReactNode;
  footer: ReactNode;
  onSelect?: () => void;
}

function Metric({ label, icon: Icon, iconClassName, value, footer, onSelect }: MetricProps) {
  const body = (
    <>
      <span className="flex items-center gap-1.5 text-xs font-medium text-surface-500">
        <Icon className={cn("h-3.5 w-3.5", iconClassName)} aria-hidden />
        {label}
      </span>
      <span className="mt-1 block text-2xl font-semibold tracking-tight text-surface-900 tabular-nums">
        {value}
      </span>
      <span className="mt-1.5 block min-h-[1.25rem] text-[13px] text-surface-500">
        {footer}
      </span>
    </>
  );
  const cellClass = "min-w-0 bg-surface-0 px-5 py-4 text-left";
  return onSelect ? (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        cellClass,
        "transition-colors duration-200 hover:bg-surface-50 dark:hover:bg-surface-100/50",
        focusRingInsetClass,
      )}
    >
      {body}
    </button>
  ) : (
    <div className={cellClass}>{body}</div>
  );
}

/**
 * Metric strip at the bottom of the house header: fleet health, guests now,
 * queued arrivals, host share. Reuses the devices-tab query (one request).
 */
export function HouseOverviewStats({
  house,
  houseId,
  showHostShare,
  showTelemetry,
  onOpenDevices,
  onOpenGuests,
}: HouseOverviewStatsProps) {
  const { t } = useTranslation("houses");
  const { data: devices, isError } = useQuery({
    queryKey: deviceKeys.byHouse(houseId),
    queryFn: () => houseService.listDevices(houseId),
    enabled: !!houseId,
  });

  // Null until the devices arrive: loading and failure show placeholders, never zeros.
  const summary = useMemo(() => {
    if (!devices) {
      return null;
    }
    const now = Date.now();
    const statuses = devices
      .map((d) => getTelemetryStatus(d, now))
      .sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b));
    const occupied = devices.filter((d) => d.current_presence != null).length;
    return {
      total: devices.length,
      statuses,
      online: statuses.filter((s) => s === "green").length,
      occupied,
      occupancyPct:
        devices.length > 0 ? Math.round((occupied / devices.length) * 100) : 0,
      upcoming: devices.reduce(
        (sum, d) => sum + Math.max(0, d.upcoming_presence_count ?? 0),
        0,
      ),
    };
  }, [devices]);

  const pending = "—";
  // The devices tab (one click away) shows the same failure with a retry.
  const unavailableFooter = isError ? (
    <span className="line-clamp-1 text-error-600">
      {t("loadDevices", { ns: "errors" })}
    </span>
  ) : null;

  return (
    <div
      className={cn(
        "grid gap-px border-t border-surface-200 bg-surface-200",
        showHostShare ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-3",
      )}
    >
      <Metric
        label={t("detail.overview.devices")}
        icon={Tablet}
        iconClassName="text-primary-600"
        value={summary ? summary.total : pending}
        onSelect={onOpenDevices}
        footer={
          !summary ? (
            unavailableFooter
          ) : showTelemetry ? (
            <span className="flex min-w-0 items-center gap-2">
              {summary.statuses.length > 0 ? (
                <span className="flex shrink-0 items-center gap-0.5" aria-hidden>
                  {summary.statuses.slice(0, MAX_STATUS_DOTS).map((status, i) => (
                    <span
                      key={i}
                      className={cn("h-2 w-2 rounded-full", telemetryStatusDotClass(status))}
                    />
                  ))}
                </span>
              ) : null}
              <span className="truncate">
                {t("detail.overview.devicesOnline", { count: summary.online })}
              </span>
            </span>
          ) : (
            <span className="line-clamp-1">{t("detail.overview.devicesHint")}</span>
          )
        }
      />
      <Metric
        label={t("detail.overview.guestsNow")}
        icon={Users}
        iconClassName="text-success-600"
        value={
          summary ? (
            <>
              {summary.occupied}
              <span className="text-base font-medium text-surface-400">
                {" / "}
                {summary.total}
              </span>
            </>
          ) : (
            pending
          )
        }
        onSelect={onOpenGuests}
        footer={
          summary ? (
            <span className="flex items-center gap-2">
              <span className="hidden h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-surface-200 sm:block" aria-hidden>
                <span
                  className="block h-full rounded-full bg-success-500 transition-all duration-500"
                  style={{ width: `${summary.occupancyPct}%` }}
                />
              </span>
              <span className="truncate">
                {t("detail.overview.guestsNowHint", { count: summary.total })}
              </span>
            </span>
          ) : null
        }
      />
      <Metric
        label={t("detail.overview.upcoming")}
        icon={CalendarClock}
        iconClassName="text-accent-600"
        value={summary ? summary.upcoming : pending}
        onSelect={onOpenGuests}
        footer={<span className="line-clamp-1">{t("detail.overview.upcomingHint")}</span>}
      />
      {showHostShare ? (
        <Metric
          label={t("detail.overview.hostShare")}
          icon={Percent}
          iconClassName="text-warning-600"
          value={`${commissionPercentStringFromRate(house.commission_rate)}%`}
          footer={<span className="line-clamp-1">{t("detail.overview.hostShareHint")}</span>}
        />
      ) : null}
    </div>
  );
}
